import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Mail, Upload, Loader2, Play, Eye, X, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_MAP = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: FileText },
  processing: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  processed: { label: 'Traitée', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  error: { label: 'Erreur', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  ignored: { label: 'Ignorée', color: 'bg-slate-100 text-slate-500', icon: X },
};

export default function CollecteEmail() {
  const [config, setConfig] = useState(null);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState('');
  const [configForm, setConfigForm] = useState({ is_active: true, email_address: '', imap_host: '', imap_port: 993, imap_username: '', imap_password: '', imap_use_ssl: true, auto_process: true, folder: 'INBOX' });

  const fetchAll = async () => {
    try {
      const [cfgRes, qRes, stRes] = await Promise.all([
        axios.get(`${API_URL}/collecte/config`),
        axios.get(`${API_URL}/collecte/queue`),
        axios.get(`${API_URL}/collecte/stats`),
      ]);
      setConfig(cfgRes.data); setQueue(qRes.data); setStats(stRes.data);
      if (cfgRes.data) setConfigForm({ ...configForm, ...cfgRes.data });
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/collecte/config`, configForm);
      toast.success('Configuration sauvegardée');
      setConfigOpen(false);
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const processItem = async (itemId) => {
    setProcessing(itemId);
    try {
      const res = await axios.post(`${API_URL}/collecte/queue/${itemId}/process`);
      toast.success(`Facture traitée${res.data.invoice_id ? ' — brouillon créé' : ''}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur traitement'); }
    finally { setProcessing(''); }
  };

  const ignoreItem = async (itemId) => {
    try { await axios.post(`${API_URL}/collecte/queue/${itemId}/ignore`); fetchAll(); } catch (e) {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/collecte/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Fichier ajouté à la file d\'attente');
      fetchAll();
    } catch (e) { toast.error('Erreur upload'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold"><Mail className="w-6 h-6 inline mr-2" />Collecte des factures</h1>
          <p className="text-slate-500">Importation automatique par email ou upload manuel</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setConfigOpen(true)}>Configurer</Button>
          <label>
            <Button asChild className="bg-[#0F5156] hover:bg-[#0d464a] cursor-pointer"><span><Upload className="w-4 h-4 mr-2" /> Upload manuel</span></Button>
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} />
          </label>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-sm text-slate-500">Total</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-amber-600">{stats.pending}</p><p className="text-sm text-slate-500">En attente</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-green-600">{stats.processed}</p><p className="text-sm text-slate-500">Traitées</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-red-600">{stats.error}</p><p className="text-sm text-slate-500">Erreurs</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-slate-400">{stats.ignored}</p><p className="text-sm text-slate-500">Ignorées</p></CardContent></Card>
        </div>
      )}

      {/* Queue */}
      <Card>
        <CardHeader><CardTitle>File d'attente</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Source</TableHead><TableHead>Fichier</TableHead><TableHead>Statut</TableHead><TableHead>Résultat</TableHead><TableHead className="w-32">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {queue.map(item => {
                const st = STATUS_MAP[item.status] || STATUS_MAP.pending;
                return (
                  <TableRow key={item.id}>
                    <TableCell className="text-sm">{item.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="text-sm">{item.email_from === 'upload_manuel' ? 'Upload' : item.email_from}</TableCell>
                    <TableCell className="text-sm">{item.filename}</TableCell>
                    <TableCell><Badge className={`${st.color} text-xs`}>{st.label}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {item.invoice_id ? 'Facture créée' : item.error_message ? item.error_message.slice(0, 50) : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {item.status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => processItem(item.id)} disabled={processing === item.id}>
                            {processing === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                            Traiter
                          </Button>
                        )}
                        {item.status === 'error' && (
                          <Button variant="ghost" size="sm" onClick={() => processItem(item.id)} disabled={processing === item.id}>
                            Réessayer
                          </Button>
                        )}
                        {['pending', 'error'].includes(item.status) && (
                          <Button variant="ghost" size="sm" onClick={() => ignoreItem(item.id)}><X className="w-3 h-3" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {queue.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">File d'attente vide</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Configuration collecte email</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2"><Switch checked={configForm.is_active} onCheckedChange={v => setConfigForm({...configForm, is_active: v})} /><Label>Activer la collecte automatique</Label></div>
            <div><Label>Adresse email dédiée</Label><Input value={configForm.email_address || ''} onChange={e => setConfigForm({...configForm, email_address: e.target.value})} placeholder="factures@votre-structure.fr" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Serveur IMAP</Label><Input value={configForm.imap_host || ''} onChange={e => setConfigForm({...configForm, imap_host: e.target.value})} placeholder="imap.gmail.com" /></div>
              <div><Label>Port</Label><Input type="number" value={configForm.imap_port} onChange={e => setConfigForm({...configForm, imap_port: parseInt(e.target.value)})} /></div>
              <div><Label>Utilisateur</Label><Input value={configForm.imap_username || ''} onChange={e => setConfigForm({...configForm, imap_username: e.target.value})} /></div>
              <div><Label>Mot de passe</Label><Input type="password" value={configForm.imap_password || ''} onChange={e => setConfigForm({...configForm, imap_password: e.target.value})} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={configForm.auto_process} onCheckedChange={v => setConfigForm({...configForm, auto_process: v})} /><Label>Traitement automatique (OCR + création brouillon)</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Annuler</Button>
            <Button onClick={saveConfig} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Sauvegarder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
